// note that "discoveryCollections" is renamed "categories" in this store for SearchOverview page
// note that "discoveryTags" is renamed "tags" in this store for SearchOverview page
import { defineStore } from 'pinia'
import { useI18n } from 'vue-i18n'
import debounce from 'lodash/debounce'

// Provide a single debounce that we can wrap around all fetch calls
const fetchDebounce = debounce((call: () => void) => call(), 300)

/**
 * Discovery search overview state.
 */
export const useDiscoveryOverviewStore = defineStore('discovery-overview', () => {
  const cacheStore = useCacheStore()
  const pageStore = usePageStore()
  const graphqlFetch = useGraphqlFetch()
  const { locale } = useI18n()

  // Global results (i.e. unaffected by search context)
  const allFacets = ref<any>({})

  // Search context
  const keyword = ref('')
  const categories = ref<string[]>([])
  const tags = ref<string[]>([])
  const page = ref(1)

  // Search results
  const discoveries = ref<any[]>([]) // our main data source
  /*
   * Raw per-search facets from Elasticsearch, named differently from the `facets` getter that exposes
   * it - Pinia shares one namespace for state and getters, so they can't share a name.
   */
  const rawFacets = ref<any>({})
  const hits = ref<string[]>([])
  const loading = ref(true)
  const totalHits = ref(0)

  // Constants
  const hitsPerPage = computed(() => 15)

  const urlParameters = computed(() => ({
    keyword: keyword.value || undefined,
    page: page.value === 1 ? undefined : page.value,
    categories: categories.value,
    tags: tags.value,
  }))
  const facets = computed(() => {
    const helper = (fromAllFacets: any[], fromFacets: any[], activeList: string[]) => {
      const currentCountMap = Object.fromEntries((fromFacets || []).map(({ count, label, value }) => [value || label, count]))
      return (fromAllFacets || []).map(({ value, label }) => ({
        active: activeList.includes(value || label),
        count: currentCountMap[value || label] || 0,
        label,
        value: value || label,
      }))
    }
    return [{ key: 'categories', values: helper(allFacets.value.categories, rawFacets.value.categories, categories.value) }]
  })
  const activeTagFacets = computed(() => {
    return [
      // 'tags' are special as we do not display them as facets to the user, but they must be removable
      ...tags.value.map((value) => ({ facet: 'tags', label: value, value })),
    ]
  })
  const activeRegularFacets = computed(() => {
    const helper = (fromAllFacets: any[], activeList: string[], facet: string) =>
      (fromAllFacets || []).filter(({ value, label }) => activeList.includes(value || label)).map(({ value, label }) => ({ facet, label, value: value || label }))
    return [...helper(allFacets.value.categories, categories.value, 'categories')]
  })

  // Global results (i.e. unaffected by search context)
  function setAllFacets(newAllFacets: any) {
    allFacets.value = newAllFacets
  }

  // Initialize
  function initialize({ keyword: kw, page: pg, categories: cats, tags: tgs }: { keyword?: any; page?: any; categories?: any; tags?: any }) {
    // When there are no values given, i.e. from the URL, we will simply keep the most recently used search context
    if (kw === undefined && pg === undefined && cats === undefined && tgs === undefined) {
      return
    }

    const toArray = (value: any) => (value === undefined ? [] : Array.isArray(value) ? value : [value])

    // For backwards compatability we fallback to the parameter 'search' for the keyword
    keyword.value = typeof kw === 'string' ? kw : ''
    page.value = typeof pg === 'string' ? Math.max(1, Number.parseInt(pg)) : 1
    categories.value = toArray(cats)
    tags.value = toArray(tgs)
  }

  // Url parameters
  function setUrlParameters(query: any) {
    const toArray = (value: any) => (value === undefined ? [] : Array.isArray(value) ? value : [value])
    const toValue = (value: any, fallback: any) => (value === undefined ? fallback : Array.isArray(value) ? value?.[0] : value)
    keyword.value = toValue(query.keyword, '')
    categories.value = toArray(query.categories)
    tags.value = toArray(query.tags)
    page.value = Math.max(1, toValue(query.page, 1))
    fetchDiscoveries()
  }
  function reset() {
    page.value = 1
    categories.value = []
    keyword.value = ''
    // note that we do NOT reset the state.tags because this is not a facet that can be set by the user
    fetchDiscoveries()
  }
  // Search context
  function setKeyword(newKeyword: string) {
    keyword.value = newKeyword
    page.value = 1
    // Debounce because we do not want to fetch on every keystroke
    fetchDebounce(() => fetchDiscoveries())
  }
  function setPage(newPage: number) {
    page.value = Math.max(1, newPage)
    fetchDiscoveries()
  }
  // only enable one type of facet value
  function enableFacet({ name, value }: { name: string; value: string }) {
    switch (name) {
      case 'categories':
        categories.value = [value]
        page.value = 1
        fetchDiscoveries()
        break
      case 'tags':
        tags.value = [value]
        page.value = 1
        fetchDiscoveries()
        break
      default:
        console.error(`Unable to enable unknown facet "${name}" with value "${value}"`)
        break
    }
  }
  // reset facet by name, as only one facet can be active
  function resetFacet({ name }: { name: string }) {
    switch (name) {
      case 'categories':
        categories.value = []
        page.value = 1
        fetchDiscoveries()
        break
      case 'tags':
        tags.value = []
        page.value = 1
        fetchDiscoveries()
        break
      default:
        console.error(`Unable to remove unknown facet "${name}"`)
        break
    }
  }
  // Search results
  function setFacets(newFacets: any) {
    rawFacets.value = newFacets
  }
  function setHits(newHits: string[]) {
    hits.value = newHits
  }
  function setTotalHits(newTotalHits: number) {
    totalHits.value = newTotalHits
  }
  function setDiscoveries(newDiscoveries: any[]) {
    discoveries.value = newDiscoveries
  }
  function setLoading(newLoading: boolean) {
    loading.value = newLoading
  }

  async function fetchAllFacets() {
    const key = `discovery-facets;${locale.value};all-facets`
    const ttl = 900

    const fetcher = async () => {
      const { elasticSearch } = await graphqlFetch({
        token: 'elasticsearch',
        query: `query allDiscoveryFacets($site:[String]) {
            elasticSearch(site:$site, section:"discovery") {
              facets(limit:1024) {
                categories: discoveryCollections { label: value }
              }
            }
          }`,
        variables: { site: `${locale.value}Default` },
      })
      return elasticSearch.facets
    }

    // Fetch the hits
    const allFacetsResult = await cacheStore.fetch({ key, ttl, fetcher, fallback: {} })
    setAllFacets(allFacetsResult)
    return allFacetsResult
  }

  async function fetchDiscoveries() {
    const orderBy = [keyword.value === '' ? 'postDate DESC' : '']

    const key = ['discoveries', locale.value, `keyword:${keyword.value}`, `categories:${categories.value.join(',')}`, `tags:${tags.value.join(',')}`, `orderBy:${orderBy}`].join(
      ';',
    )
    const ttl = 300

    const fetcher = async () => {
      // Fetch 1024 results, therefore we will not need to fetch new results when we switch pages
      const { elasticSearch } = await graphqlFetch({
        token: 'elasticsearch',
        query: `query searchDiscoveries($site: [String], $keyword: String, $discoveryCollections:[String], $discoveryTags:[String], $orderBy:[String]) {
          elasticSearch(site: $site, section: "discovery", search: $keyword, discoveryCollectionsFacet:$discoveryCollections, discoveryTagsFacet:$discoveryTags, orderBy:$orderBy) {
            facets(limit: 1024) {
              categories: discoveryCollections {
                label: value
                count
              }
            }
            count
            hits(limit: 1024) {
              uri
            }
          }
        }
        `,
        variables: {
          site: `${locale.value}Default`,
          keyword: keyword.value,
          discoveryCollections: categories.value,
          discoveryTags: tags.value,
          orderBy,
        },
      })

      return { facets: elasticSearch.facets, hits: elasticSearch.hits.map(({ uri }: { uri: string }) => uri), totalHits: elasticSearch.count }
    }

    // Fetch the hits
    const {
      facets: fetchedFacets,
      hits: fetchedHits,
      totalHits: fetchedTotalHits,
    } = await cacheStore.fetch({
      key,
      ttl,
      fetcher,
      fallback: { facets: {}, hits: [], totalHits: 0 },
    })
    setFacets(fetchedFacets)
    setHits(fetchedHits)
    setTotalHits(fetchedTotalHits)

    // todo: add loaders
    // Initialize the series list with N empty elements, used to trigger loaders
    const slice = fetchedHits.slice((page.value - 1) * hitsPerPage.value, page.value * hitsPerPage.value)

    // Fetch all associated entries within the current page
    const fetchedDiscoveries = await Promise.all(slice.map((uri: string) => pageStore.fetchUri({ uri })))
    setDiscoveries(fetchedDiscoveries.filter((discovery) => discovery))
    setLoading(false)
    return fetchedDiscoveries
  }

  return {
    allFacets,
    keyword,
    categories,
    tags,
    page,
    discoveries,
    rawFacets,
    hits,
    loading,
    totalHits,
    hitsPerPage,
    urlParameters,
    facets,
    activeTagFacets,
    activeRegularFacets,
    setAllFacets,
    initialize,
    setUrlParameters,
    reset,
    setKeyword,
    setPage,
    enableFacet,
    resetFacet,
    setFacets,
    setHits,
    setTotalHits,
    setDiscoveries,
    setLoading,
    fetchAllFacets,
    fetch: fetchDiscoveries,
  }
})
